/**
 * Child Service Report Page
 * Editable form for documenting child developmental progress
 * Auto-fills child name, birthdate, and facility name from enrolled children
 * Only shows full-time children (not school age)
 */
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import AppShell from "@/components/layout/AppShell";
import PrintReportHeader from "@/components/common/PrintReportHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Save, RotateCcw, FileText, User, PenTool, FolderOpen, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Signature font options
const SIGNATURE_FONTS = [
  { value: "cursive", label: "Cursive", fontFamily: "'Brush Script MT', 'Segoe Script', cursive" },
  { value: "dancing", label: "Dancing Script", fontFamily: "'Dancing Script', cursive" },
  { value: "pacifico", label: "Pacifico", fontFamily: "'Pacifico', cursive" },
  { value: "greatvibes", label: "Great Vibes", fontFamily: "'Great Vibes', cursive" },
  { value: "sacramento", label: "Sacramento", fontFamily: "'Sacramento', cursive" },
  { value: "allura", label: "Allura", fontFamily: "'Allura', cursive" },
  { value: "normal", label: "Normal", fontFamily: "Arial, sans-serif" },
];

const ChildServiceReportPage = () => {
  const { user, organization } = useAuth();
  const { language } = useLanguage();
  const printRef = useRef();
  
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState("");
  const [loading, setLoading] = useState(true);
  const [signatureFont, setSignatureFont] = useState("cursive");
  const [savedReports, setSavedReports] = useState({});
  const [expandedYears, setExpandedYears] = useState({});
  const [savingReport, setSavingReport] = useState(false);
  const [showSavedReports, setShowSavedReports] = useState(false);
  
  // Form data state
  const [formData, setFormData] = useState({
    childName: "",
    facilityName: "",
    birthDate: "",
    // Child's strengths section
    strengthsPhysical: "",
    strengthsKnowledge: "",
    strengthsSocialEmotional: "",
    strengthsCommunication: "",
    // Developmental milestones section
    milestonesPhysical: "",
    milestonesKnowledge: "",
    milestonesSocialEmotional: "",
    milestonesCommunication: "",
    // At home suggestions section
    atHomePhysical: "",
    atHomeKnowledge: "",
    atHomeSocialEmotional: "",
    atHomeCommunication: "",
    // Signatures - now editable
    facilityReporterSignature: "",
    facilityReporterName: "",
    facilityReporterDate: new Date().toLocaleDateString(),
    parentGuardianSignature: "",
    parentGuardianName: "",
    parentGuardianDate: ""
  });

  // Get the selected font family
  const getSignatureFontFamily = () => {
    const font = SIGNATURE_FONTS.find(f => f.value === signatureFont);
    return font ? font.fontFamily : "'Brush Script MT', cursive";
  };

  // Fetch children and saved reports on mount
  useEffect(() => {
    fetchChildren();
    fetchSavedReports();
  }, []);

  // Set facility name from organization
  useEffect(() => {
    if (organization?.name) {
      setFormData(prev => ({
        ...prev,
        facilityName: organization.name
      }));
    }
  }, [organization]);

  // Set reporter name from user
  useEffect(() => {
    if (user?.name) {
      setFormData(prev => ({
        ...prev,
        facilityReporterName: user.name
      }));
    }
  }, [user]);

  const fetchChildren = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/children`);
      // Filter: active children only, exclude school-age (only show full-time)
      const activeChildren = (response.data || []).filter(c => {
        if (c.deleted) return false;
        // Exclude school-age children - only show full-time
        const category = (c.category || '').toLowerCase();
        const subcategory = (c.fullTimeSubcategory || '').toLowerCase();
        // Include if category is 'full_time' or similar, exclude 'school_age'
        if (category.includes('school') || subcategory.includes('school')) return false;
        return true;
      });
      setChildren(activeChildren);
    } catch (error) {
      console.error("Error fetching children:", error);
    } finally {
      setLoading(false);
    }
  };

  // Storage key for localStorage - kept for migration purposes
  const STORAGE_KEY = 'childServiceReports';

  // Load saved reports from API
  const fetchSavedReports = async () => {
    try {
      const response = await axios.get(`${API}/child-service-reports`);
      const reports = response.data || [];
      
      // Organize reports by year
      const reportsByYear = {};
      reports.forEach(report => {
        const year = report.year || new Date(report.savedAt || report.createdAt).getFullYear();
        if (!reportsByYear[year]) {
          reportsByYear[year] = [];
        }
        reportsByYear[year].push(report);
      });
      setSavedReports(reportsByYear);
      
      // Check for and migrate localStorage data (one-time migration)
      migrateLocalStorageData();
    } catch (error) {
      console.error("Error loading saved reports:", error);
      // Fallback to localStorage if API fails
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        const reports = stored ? JSON.parse(stored) : [];
        const reportsByYear = {};
        reports.forEach(report => {
          const year = report.year || new Date(report.savedAt || report.createdAt).getFullYear();
          if (!reportsByYear[year]) {
            reportsByYear[year] = [];
          }
          reportsByYear[year].push(report);
        });
        setSavedReports(reportsByYear);
      } catch (e) {
        console.error("Error loading from localStorage:", e);
      }
    }
  };

  // Migrate localStorage data to API (one-time)
  const migrateLocalStorageData = async () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;
      
      const localReports = JSON.parse(stored);
      if (localReports.length === 0) return;
      
      // Migrate each report to the API
      let migratedCount = 0;
      for (const report of localReports) {
        try {
          await axios.post(`${API}/child-service-reports`, {
            childId: report.childId,
            childName: report.childName,
            facilityName: report.facilityName,
            birthDate: report.birthDate,
            strengthsPhysical: report.strengthsPhysical,
            strengthsKnowledge: report.strengthsKnowledge,
            strengthsSocialEmotional: report.strengthsSocialEmotional,
            strengthsCommunication: report.strengthsCommunication,
            milestonesPhysical: report.milestonesPhysical,
            milestonesKnowledge: report.milestonesKnowledge,
            milestonesSocialEmotional: report.milestonesSocialEmotional,
            milestonesCommunication: report.milestonesCommunication,
            atHomePhysical: report.atHomePhysical,
            atHomeKnowledge: report.atHomeKnowledge,
            atHomeSocialEmotional: report.atHomeSocialEmotional,
            atHomeCommunication: report.atHomeCommunication,
            facilityReporterSignature: report.facilityReporterSignature,
            facilityReporterName: report.facilityReporterName,
            facilityReporterDate: report.facilityReporterDate,
            parentGuardianSignature: report.parentGuardianSignature,
            parentGuardianName: report.parentGuardianName,
            parentGuardianDate: report.parentGuardianDate,
            signatureFont: report.signatureFont
          });
          migratedCount++;
        } catch (e) {
          console.error("Error migrating report:", e);
        }
      }
      
      if (migratedCount > 0) {
        // Clear localStorage after successful migration
        localStorage.removeItem(STORAGE_KEY);
        toast.success(`Migrated ${migratedCount} reports to server`);
        // Refresh the reports list
        const response = await axios.get(`${API}/child-service-reports`);
        const reports = response.data || [];
        const reportsByYear = {};
        reports.forEach(report => {
          const year = report.year || new Date(report.savedAt || report.createdAt).getFullYear();
          if (!reportsByYear[year]) {
            reportsByYear[year] = [];
          }
          reportsByYear[year].push(report);
        });
        setSavedReports(reportsByYear);
      }
    } catch (error) {
      console.error("Error migrating localStorage data:", error);
    }
  };

  // Save report to API
  const handleSaveReport = async () => {
    if (!formData.childName) {
      toast.error(language === 'es' ? "Seleccione un niño primero" : "Please select a child first");
      return;
    }
    
    // Require a date in the signature section to determine the year folder
    if (!formData.facilityReporterDate) {
      toast.error(language === 'es' ? "Por favor ingrese la fecha en la sección de firma" : "Please enter the date in the signature section");
      return;
    }
    
    try {
      setSavingReport(true);
      
      // Create report data
      const reportData = {
        childId: selectedChildId,
        childName: formData.childName,
        facilityName: formData.facilityName,
        birthDate: formData.birthDate,
        strengthsPhysical: formData.strengthsPhysical,
        strengthsKnowledge: formData.strengthsKnowledge,
        strengthsSocialEmotional: formData.strengthsSocialEmotional,
        strengthsCommunication: formData.strengthsCommunication,
        milestonesPhysical: formData.milestonesPhysical,
        milestonesKnowledge: formData.milestonesKnowledge,
        milestonesSocialEmotional: formData.milestonesSocialEmotional,
        milestonesCommunication: formData.milestonesCommunication,
        atHomePhysical: formData.atHomePhysical,
        atHomeKnowledge: formData.atHomeKnowledge,
        atHomeSocialEmotional: formData.atHomeSocialEmotional,
        atHomeCommunication: formData.atHomeCommunication,
        facilityReporterSignature: formData.facilityReporterSignature,
        facilityReporterName: formData.facilityReporterName,
        facilityReporterDate: formData.facilityReporterDate,
        parentGuardianSignature: formData.parentGuardianSignature,
        parentGuardianName: formData.parentGuardianName,
        parentGuardianDate: formData.parentGuardianDate,
        signatureFont: signatureFont
      };
      
      const response = await axios.post(`${API}/child-service-reports`, reportData);
      const savedYear = response.data.year || new Date().getFullYear();
      
      toast.success(language === 'es' ? `Informe guardado en ${savedYear}` : `Report saved to ${savedYear}`);
      fetchSavedReports();
    } catch (error) {
      console.error("Error saving report:", error);
      toast.error(language === 'es' ? "Error al guardar el informe" : "Error saving report");
    } finally {
      setSavingReport(false);
    }
  };

  // Load a saved report
  const handleLoadReport = (report) => {
    setSelectedChildId(report.childId || "");
    setSignatureFont(report.signatureFont || "cursive");
    setFormData({
      childName: report.childName || "",
      facilityName: report.facilityName || organization?.name || "",
      birthDate: report.birthDate || "",
      strengthsPhysical: report.strengthsPhysical || "",
      strengthsKnowledge: report.strengthsKnowledge || "",
      strengthsSocialEmotional: report.strengthsSocialEmotional || "",
      strengthsCommunication: report.strengthsCommunication || "",
      milestonesPhysical: report.milestonesPhysical || "",
      milestonesKnowledge: report.milestonesKnowledge || "",
      milestonesSocialEmotional: report.milestonesSocialEmotional || "",
      milestonesCommunication: report.milestonesCommunication || "",
      atHomePhysical: report.atHomePhysical || "",
      atHomeKnowledge: report.atHomeKnowledge || "",
      atHomeSocialEmotional: report.atHomeSocialEmotional || "",
      atHomeCommunication: report.atHomeCommunication || "",
      facilityReporterSignature: report.facilityReporterSignature || "",
      facilityReporterName: report.facilityReporterName || "",
      facilityReporterDate: report.facilityReporterDate || "",
      parentGuardianSignature: report.parentGuardianSignature || "",
      parentGuardianName: report.parentGuardianName || "",
      parentGuardianDate: report.parentGuardianDate || ""
    });
    setShowSavedReports(false);
    toast.success(language === 'es' ? "Informe cargado" : "Report loaded");
  };

  // Delete a saved report from API
  const handleDeleteReport = async (reportId, e) => {
    e.stopPropagation();
    if (!window.confirm(language === 'es' ? "¿Eliminar este informe?" : "Delete this report?")) return;
    
    try {
      await axios.delete(`${API}/child-service-reports/${reportId}`);
      toast.success(language === 'es' ? "Informe eliminado" : "Report deleted");
      fetchSavedReports();
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error(language === 'es' ? "Error al eliminar" : "Error deleting report");
    }
  };

  // Toggle year expansion
  const toggleYear = (year) => {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };

  // Handle child selection - auto-fill name and birthdate
  const handleChildSelect = (childId) => {
    setSelectedChildId(childId);
    const child = children.find(c => (c.id || c._id) === childId);
    if (child) {
      setFormData(prev => ({
        ...prev,
        childName: child.name || `${child.firstName || ''} ${child.lastName || ''}`.trim(),
        birthDate: child.birthdate ? formatDate(child.birthdate) : ""
      }));
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Reset form
  const handleReset = () => {
    setSelectedChildId("");
    setFormData({
      childName: "",
      facilityName: organization?.name || "",
      birthDate: "",
      strengthsPhysical: "",
      strengthsKnowledge: "",
      strengthsSocialEmotional: "",
      strengthsCommunication: "",
      milestonesPhysical: "",
      milestonesKnowledge: "",
      milestonesSocialEmotional: "",
      milestonesCommunication: "",
      atHomePhysical: "",
      atHomeKnowledge: "",
      atHomeSocialEmotional: "",
      atHomeCommunication: "",
      facilityReporterSignature: "",
      facilityReporterName: user?.name || "",
      facilityReporterDate: new Date().toLocaleDateString(),
      parentGuardianSignature: "",
      parentGuardianName: "",
      parentGuardianDate: ""
    });
  };

  // Print the form
  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    const signatureFontFamily = getSignatureFontFamily();
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Child Service Report - ${formData.childName || 'Form'}</title>
          <link rel="preconnect" href="https://fonts.googleapis.com">
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
          <link href="https://fonts.googleapis.com/css2?family=Allura&family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Pacifico&family=Sacramento&display=swap" rel="stylesheet">
          <style>
            @page {
              size: 8.5in 11in;
              margin: 0.5in;
            }
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 11px;
              line-height: 1.4;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .form-container {
              max-width: 8in;
              margin: 0 auto;
              padding: 20px;
            }
            .logo-header {
              margin-bottom: 8px;
            }
            .logo-header img {
              height: 40px;
              width: auto;
            }
            .center-name {
              text-align: center;
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 12px;
            }
            .header {
              text-align: center;
              margin-bottom: 15px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            .header h1 {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .header-row {
              display: flex;
              justify-content: space-between;
              margin-top: 10px;
              gap: 20px;
            }
            .header-field {
              flex: 1;
            }
            .header-field label {
              font-weight: bold;
            }
            .header-field .value {
              border-bottom: 1px solid #000;
              min-height: 18px;
              padding: 2px 5px;
            }
            .section {
              margin-bottom: 15px;
            }
            .section-title {
              font-weight: bold;
              font-size: 12px;
              margin-bottom: 8px;
              background: #f0f0f0;
              padding: 5px;
            }
            .field-row {
              margin-bottom: 10px;
            }
            .field-label {
              font-weight: bold;
              font-size: 10px;
              margin-bottom: 3px;
            }
            .field-value {
              border: 1px solid #ccc;
              min-height: 50px;
              padding: 5px;
              white-space: pre-wrap;
            }
            .signature-section {
              margin-top: 20px;
              padding-top: 15px;
              border-top: 1px solid #000;
            }
            .signature-row {
              display: flex;
              gap: 20px;
              margin-bottom: 10px;
            }
            .signature-field {
              flex: 1;
            }
            .signature-field label {
              font-weight: bold;
              font-size: 10px;
            }
            .signature-field .value {
              border-bottom: 1px solid #000;
              min-height: 25px;
              margin-top: 5px;
              padding: 2px 5px;
            }
            .signature-value {
              font-family: ${signatureFontFamily};
              font-size: 18px;
              color: #1a365d;
            }
            @media print {
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          <div class="form-container">
            <div class="logo-header">
              <img src="/simple-sign-in-logo.png" alt="Simple Sign In" onerror="this.style.display='none'" />
            </div>
            <div class="center-name">${formData.facilityName}</div>
            <div class="header">
              <h1>CHILD SERVICE REPORT</h1>
              <div class="header-row">
                <div class="header-field">
                  <label>Child's Name:</label>
                  <div class="value">${formData.childName}</div>
                </div>
                <div class="header-field">
                  <label>Birth Date:</label>
                  <div class="value">${formData.birthDate}</div>
                </div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">Your child's strengths, as age appropriate, in the following areas are:</div>
              <div class="field-row">
                <div class="field-label">Physical (fine motor and gross motor):</div>
                <div class="field-value">${formData.strengthsPhysical}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Knowledge and Skills (approaches to learning, math, science and social studies):</div>
                <div class="field-value">${formData.strengthsKnowledge}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Social Emotional (personal-social):</div>
                <div class="field-value">${formData.strengthsSocialEmotional}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Communication, Language and Literacy:</div>
                <div class="field-value">${formData.strengthsCommunication}</div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">The next developmental milestones, as age appropriate, we're working on are:</div>
              <div class="field-row">
                <div class="field-label">Physical (fine motor and gross motor):</div>
                <div class="field-value">${formData.milestonesPhysical}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Knowledge and Skills (approaches to learning, math, science and social studies):</div>
                <div class="field-value">${formData.milestonesKnowledge}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Social Emotional (personal-social):</div>
                <div class="field-value">${formData.milestonesSocialEmotional}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Communication, Language and Literacy:</div>
                <div class="field-value">${formData.milestonesCommunication}</div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">You can help your child grow and develop, as age appropriate, at home by:</div>
              <div class="field-row">
                <div class="field-label">Physical (fine motor and gross motor):</div>
                <div class="field-value">${formData.atHomePhysical}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Knowledge and Skills (approaches to learning, math, science and social studies):</div>
                <div class="field-value">${formData.atHomeKnowledge}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Social Emotional (personal-social):</div>
                <div class="field-value">${formData.atHomeSocialEmotional}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Communication, Language and Literacy:</div>
                <div class="field-value">${formData.atHomeCommunication}</div>
              </div>
            </div>

            <div class="signature-section">
              <div class="section-title">Facility person who completed this child's report:</div>
              <div class="signature-row">
                <div class="signature-field">
                  <label>Signature:</label>
                  <div class="value"><span class="signature-value">${formData.facilityReporterSignature}</span></div>
                </div>
                <div class="signature-field">
                  <label>Name:</label>
                  <div class="value">${formData.facilityReporterName}</div>
                </div>
                <div class="signature-field">
                  <label>Date:</label>
                  <div class="value">${formData.facilityReporterDate}</div>
                </div>
              </div>
            </div>

            <div class="signature-section">
              <div class="section-title">This report was reviewed with and a copy given to the following parent/guardian:</div>
              <div class="signature-row">
                <div class="signature-field">
                  <label>Signature:</label>
                  <div class="value"><span class="signature-value">${formData.parentGuardianSignature}</span></div>
                </div>
                <div class="signature-field">
                  <label>Name:</label>
                  <div class="value">${formData.parentGuardianName}</div>
                </div>
                <div class="signature-field">
                  <label>Date:</label>
                  <div class="value">${formData.parentGuardianDate}</div>
                </div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return (
    <AppShell pageTitle={language === 'es' ? "Informe de Servicio del Niño" : "Child Service Report"} breadcrumb>
      <div className="p-4 md:p-6 space-y-6">
        {/* Print Header - Only visible when printing */}
        <PrintReportHeader reportTitle="Child Service Report" />
        
        {/* Header Card with Child Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {language === 'es' ? "Informe de Servicio del Niño" : "Child Service Report"}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSavedReports(!showSavedReports)}
                data-testid="toggle-saved-reports"
              >
                <FolderOpen className="w-4 h-4 mr-2" />
                {language === 'es' ? "Informes Guardados" : "Saved Reports"}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Saved Reports Panel */}
            {showSavedReports && (
              <div className="bg-gray-50 border rounded-lg p-4 mb-4">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" />
                  {language === 'es' ? "Informes Guardados por Año" : "Saved Reports by Year"}
                </h3>
                {Object.keys(savedReports).length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    {language === 'es' ? "No hay informes guardados" : "No saved reports yet"}
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {Object.keys(savedReports).sort((a, b) => b - a).map(year => (
                      <div key={year} className="border rounded bg-white">
                        <button
                          onClick={() => toggleYear(year)}
                          className="w-full flex items-center justify-between p-2 hover:bg-gray-50"
                        >
                          <span className="font-medium flex items-center gap-2">
                            {expandedYears[year] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            {year}
                          </span>
                          <span className="text-sm text-gray-500">
                            {savedReports[year].length} {language === 'es' ? "informes" : "reports"}
                          </span>
                        </button>
                        {expandedYears[year] && (
                          <div className="border-t divide-y">
                            {savedReports[year].map(report => (
                              <div
                                key={report.id || report._id}
                                onClick={() => handleLoadReport(report)}
                                className="p-2 pl-8 hover:bg-blue-50 cursor-pointer flex items-center justify-between"
                              >
                                <div>
                                  <span className="font-medium">{report.childName}</span>
                                  <span className="text-xs text-gray-500 ml-2">
                                    {new Date(report.savedAt || report.createdAt).toLocaleDateString()}
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => handleDeleteReport(report.id || report._id, e)}
                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Child Selection */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-4 h-4 text-blue-600" />
                <Label className="text-blue-800 font-medium">
                  {language === 'es' ? "Seleccionar Niño (Solo Tiempo Completo)" : "Select Child (Full-Time Only)"}
                </Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <Select value={selectedChildId} onValueChange={handleChildSelect}>
                    <SelectTrigger data-testid="child-select">
                      <SelectValue placeholder={language === 'es' ? "Seleccionar un niño..." : "Select a child..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {children.map(child => (
                        <SelectItem key={child.id || child._id} value={child.id || child._id}>
                          {child.name || `${child.firstName || ''} ${child.lastName || ''}`.trim()}
                          {child.birthdate && ` (${formatDate(child.birthdate)})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={handleReset} 
                    className="flex-1" 
                    data-testid="clear-form-btn"
                    title={language === 'es' ? "Limpiar todos los campos del formulario" : "Clear all form fields"}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {language === 'es' ? "Limpiar" : "Clear"}
                  </Button>
                  <Button 
                    onClick={handleSaveReport} 
                    className="flex-1 bg-green-600 hover:bg-green-700" 
                    disabled={savingReport || !formData.childName}
                    data-testid="save-report-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingReport ? "..." : (language === 'es' ? "Guardar" : "Save")}
                  </Button>
                </div>
                <div>
                  <Button onClick={handlePrint} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="print-report-btn">
                    <Printer className="w-4 h-4 mr-2" />
                    {language === 'es' ? "Imprimir" : "Print"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Form Header Fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
              <div className="space-y-2">
                <Label>{language === 'es' ? "Nombre del Niño" : "Child's Name"}</Label>
                <Input
                  value={formData.childName}
                  onChange={(e) => handleChange('childName', e.target.value)}
                  placeholder={language === 'es' ? "Nombre del niño..." : "Child's name..."}
                  data-testid="child-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'es' ? "Nombre del Centro" : "Facility Name"}</Label>
                <Input
                  value={formData.facilityName}
                  onChange={(e) => handleChange('facilityName', e.target.value)}
                  placeholder={language === 'es' ? "Nombre del centro..." : "Facility name..."}
                  data-testid="facility-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'es' ? "Fecha de Nacimiento" : "Birth Date"}</Label>
                <Input
                  value={formData.birthDate}
                  onChange={(e) => handleChange('birthDate', e.target.value)}
                  placeholder="MM/DD/YYYY"
                  data-testid="birth-date-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 1: Child's Strengths */}
        <Card>
          <CardHeader className="bg-green-50">
            <CardTitle className="text-green-800 text-base">
              {language === 'es' 
                ? "Las fortalezas de su hijo, según su edad, en las siguientes áreas son:"
                : "Your child's strengths, as age appropriate, in the following areas are:"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="font-semibold">
                {language === 'es' ? "Físico (motricidad fina y gruesa):" : "Physical (fine motor and gross motor):"}
              </Label>
              <Textarea
                value={formData.strengthsPhysical}
                onChange={(e) => handleChange('strengthsPhysical', e.target.value)}
                rows={3}
                data-testid="strengths-physical"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">
                {language === 'es' 
                  ? "Conocimientos y Habilidades (enfoques de aprendizaje, matemáticas, ciencias y estudios sociales):"
                  : "Knowledge and Skills (approaches to learning, math, science and social studies):"}
              </Label>
              <Textarea
                value={formData.strengthsKnowledge}
                onChange={(e) => handleChange('strengthsKnowledge', e.target.value)}
                rows={3}
                data-testid="strengths-knowledge"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">
                {language === 'es' ? "Social Emocional (personal-social):" : "Social Emotional (personal-social):"}
              </Label>
              <Textarea
                value={formData.strengthsSocialEmotional}
                onChange={(e) => handleChange('strengthsSocialEmotional', e.target.value)}
                rows={3}
                data-testid="strengths-social"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">
                {language === 'es' ? "Comunicación, Lenguaje y Alfabetización:" : "Communication, Language and Literacy:"}
              </Label>
              <Textarea
                value={formData.strengthsCommunication}
                onChange={(e) => handleChange('strengthsCommunication', e.target.value)}
                rows={3}
                data-testid="strengths-communication"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Developmental Milestones */}
        <Card>
          <CardHeader className="bg-blue-50">
            <CardTitle className="text-blue-800 text-base">
              {language === 'es'
                ? "Los próximos hitos del desarrollo, según su edad, en los que estamos trabajando son:"
                : "The next developmental milestones, as age appropriate, we're working on are:"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="font-semibold">
                {language === 'es' ? "Físico (motricidad fina y gruesa):" : "Physical (fine motor and gross motor):"}
              </Label>
              <Textarea
                value={formData.milestonesPhysical}
                onChange={(e) => handleChange('milestonesPhysical', e.target.value)}
                rows={3}
                data-testid="milestones-physical"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">
                {language === 'es'
                  ? "Conocimientos y Habilidades (enfoques de aprendizaje, matemáticas, ciencias y estudios sociales):"
                  : "Knowledge and Skills (approaches to learning, math, science and social studies):"}
              </Label>
              <Textarea
                value={formData.milestonesKnowledge}
                onChange={(e) => handleChange('milestonesKnowledge', e.target.value)}
                rows={3}
                data-testid="milestones-knowledge"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">
                {language === 'es' ? "Social Emocional (personal-social):" : "Social Emotional (personal-social):"}
              </Label>
              <Textarea
                value={formData.milestonesSocialEmotional}
                onChange={(e) => handleChange('milestonesSocialEmotional', e.target.value)}
                rows={3}
                data-testid="milestones-social"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">
                {language === 'es' ? "Comunicación, Lenguaje y Alfabetización:" : "Communication, Language and Literacy:"}
              </Label>
              <Textarea
                value={formData.milestonesCommunication}
                onChange={(e) => handleChange('milestonesCommunication', e.target.value)}
                rows={3}
                data-testid="milestones-communication"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 3: At Home Suggestions */}
        <Card>
          <CardHeader className="bg-purple-50">
            <CardTitle className="text-purple-800 text-base">
              {language === 'es'
                ? "Puede ayudar a su hijo a crecer y desarrollarse, según su edad, en casa:"
                : "You can help your child grow and develop, as age appropriate, at home by:"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="font-semibold">
                {language === 'es' ? "Físico (motricidad fina y gruesa):" : "Physical (fine motor and gross motor):"}
              </Label>
              <Textarea
                value={formData.atHomePhysical}
                onChange={(e) => handleChange('atHomePhysical', e.target.value)}
                rows={3}
                data-testid="athome-physical"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">
                {language === 'es'
                  ? "Conocimientos y Habilidades (enfoques de aprendizaje, matemáticas, ciencias y estudios sociales):"
                  : "Knowledge and Skills (approaches to learning, math, science and social studies):"}
              </Label>
              <Textarea
                value={formData.atHomeKnowledge}
                onChange={(e) => handleChange('atHomeKnowledge', e.target.value)}
                rows={3}
                data-testid="athome-knowledge"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">
                {language === 'es' ? "Social Emocional (personal-social):" : "Social Emotional (personal-social):"}
              </Label>
              <Textarea
                value={formData.atHomeSocialEmotional}
                onChange={(e) => handleChange('atHomeSocialEmotional', e.target.value)}
                rows={3}
                data-testid="athome-social"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">
                {language === 'es' ? "Comunicación, Lenguaje y Alfabetización:" : "Communication, Language and Literacy:"}
              </Label>
              <Textarea
                value={formData.atHomeCommunication}
                onChange={(e) => handleChange('atHomeCommunication', e.target.value)}
                rows={3}
                data-testid="athome-communication"
              />
            </div>
          </CardContent>
        </Card>

        {/* Signature Sections */}
        <Card>
          <CardHeader className="bg-gray-50">
            <CardTitle className="text-gray-800 text-base flex items-center justify-between">
              <span>{language === 'es' ? "Firmas" : "Signatures"}</span>
              <div className="flex items-center gap-2">
                <PenTool className="w-4 h-4 text-gray-500" />
                <Label className="text-sm font-normal text-gray-600">
                  {language === 'es' ? "Estilo de Firma:" : "Signature Style:"}
                </Label>
                <Select value={signatureFont} onValueChange={setSignatureFont}>
                  <SelectTrigger className="w-40 h-8" data-testid="signature-font-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SIGNATURE_FONTS.map(font => (
                      <SelectItem key={font.value} value={font.value}>
                        <span style={{ fontFamily: font.fontFamily }}>{font.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 pt-4">
            {/* Google Fonts for signatures */}
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            <link href="https://fonts.googleapis.com/css2?family=Allura&family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Pacifico&family=Sacramento&display=swap" rel="stylesheet" />
            
            {/* Facility Reporter */}
            <div className="space-y-3">
              <Label className="font-semibold text-gray-700">
                {language === 'es' 
                  ? "Persona del centro que completó este informe del niño:"
                  : "Facility person who completed this child's report:"}
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">{language === 'es' ? "Firma Digital" : "Digital Signature"}</Label>
                  <Input
                    value={formData.facilityReporterSignature}
                    onChange={(e) => handleChange('facilityReporterSignature', e.target.value)}
                    placeholder={language === 'es' ? "Escriba su nombre como firma..." : "Type your name as signature..."}
                    className="h-12 text-xl text-blue-900"
                    style={{ fontFamily: getSignatureFontFamily() }}
                    data-testid="reporter-signature"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">{language === 'es' ? "Nombre" : "Name"}</Label>
                  <Input
                    value={formData.facilityReporterName}
                    onChange={(e) => handleChange('facilityReporterName', e.target.value)}
                    data-testid="reporter-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">{language === 'es' ? "Fecha" : "Date"}</Label>
                  <Input
                    type="date"
                    value={formData.facilityReporterDate ? new Date(formData.facilityReporterDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleChange('facilityReporterDate', e.target.value ? new Date(e.target.value).toLocaleDateString() : '')}
                    data-testid="reporter-date"
                  />
                </div>
              </div>
              {/* Signature Preview */}
              {formData.facilityReporterSignature && (
                <div className="mt-2 p-3 bg-gray-50 rounded border">
                  <Label className="text-xs text-gray-500 block mb-1">
                    {language === 'es' ? "Vista previa de firma:" : "Signature Preview:"}
                  </Label>
                  <div 
                    className="text-2xl text-blue-900"
                    style={{ fontFamily: getSignatureFontFamily() }}
                  >
                    {formData.facilityReporterSignature}
                  </div>
                </div>
              )}
            </div>

            {/* Parent/Guardian */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="font-semibold text-gray-700">
                {language === 'es'
                  ? "Este informe fue revisado y se entregó una copia al siguiente padre/tutor:"
                  : "This report was reviewed with and a copy given to the following parent/guardian:"}
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">{language === 'es' ? "Firma Digital" : "Digital Signature"}</Label>
                  <Input
                    value={formData.parentGuardianSignature}
                    onChange={(e) => handleChange('parentGuardianSignature', e.target.value)}
                    placeholder={language === 'es' ? "Escriba su nombre como firma..." : "Type your name as signature..."}
                    className="h-12 text-xl text-blue-900"
                    style={{ fontFamily: getSignatureFontFamily() }}
                    data-testid="parent-signature"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">{language === 'es' ? "Nombre" : "Name"}</Label>
                  <Input
                    value={formData.parentGuardianName}
                    onChange={(e) => handleChange('parentGuardianName', e.target.value)}
                    data-testid="parent-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">{language === 'es' ? "Fecha" : "Date"}</Label>
                  <Input
                    type="date"
                    value={formData.parentGuardianDate ? new Date(formData.parentGuardianDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleChange('parentGuardianDate', e.target.value ? new Date(e.target.value).toLocaleDateString() : '')}
                    data-testid="parent-date"
                  />
                </div>
              </div>
              {/* Signature Preview */}
              {formData.parentGuardianSignature && (
                <div className="mt-2 p-3 bg-gray-50 rounded border">
                  <Label className="text-xs text-gray-500 block mb-1">
                    {language === 'es' ? "Vista previa de firma:" : "Signature Preview:"}
                  </Label>
                  <div 
                    className="text-2xl text-blue-900"
                    style={{ fontFamily: getSignatureFontFamily() }}
                  >
                    {formData.parentGuardianSignature}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Print Button at Bottom */}
        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={handleReset} data-testid="reset-bottom-btn">
            <RotateCcw className="w-4 h-4 mr-2" />
            {language === 'es' ? "Limpiar Formulario" : "Reset Form"}
          </Button>
          <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700" data-testid="print-bottom-btn">
            <Printer className="w-4 h-4 mr-2" />
            {language === 'es' ? "Imprimir Informe" : "Print Report"}
          </Button>
        </div>
      </div>
    </AppShell>
  );
};

export default ChildServiceReportPage;
